import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { HardDrive, Trash2, AlertTriangle } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@clerk/react";

const BROWSER_KEYS: { key: string; label: string }[] = [
  { key: "rheumhub_role", label: "Your role" },
  { key: "rheumhub_ai_consent_v1", label: "AI consent given" },
  { key: "rheumhub_clinical_safety_ack_v1", label: "Clinical safety acknowledgement" },
  { key: "rheumhub_practice_level", label: "Practice level" },
  { key: "rheumhub_storage_notice_dismissed", label: "PECR storage notice dismissed" },
];

function readKey(key: string): string {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return "Not set";
    if (val === "1" || val === "true") return "Yes";
    if (val === "false") return "No";
    return val;
  } catch {
    return "Unavailable";
  }
}

interface BrowserStorageCardProps {
  cardClassName?: string;
  /** When true the card is shown outside a settings page (e.g. privacy policy).
   *  Adjusts the footer note and post-clear redirect accordingly. */
  standalone?: boolean;
}

export function BrowserStorageCard({ cardClassName, standalone }: BrowserStorageCardProps) {
  const [, navigate] = useLocation();
  const { isSignedIn } = useAuth();
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    const snapshot: Record<string, string> = {};
    for (const { key } of BROWSER_KEYS) {
      snapshot[key] = readKey(key);
    }
    setValues(snapshot);
  }, []);

  const handleClear = () => {
    try {
      for (const { key } of BROWSER_KEYS) {
        localStorage.removeItem(key);
      }
    } catch {
      // ignore if localStorage unavailable
    }
    if (standalone) {
      navigate("/");
    } else {
      // Append ?recovered=1 when the user was signed in so choose-role can
      // show a personalised "welcome back" prompt.
      navigate(isSignedIn ? "/choose-role?recovered=1" : "/choose-role");
    }
  };

  return (
    <section
      className={
        cardClassName ??
        "bg-white rounded-2xl border border-slate-200 p-8 space-y-4"
      }
    >
      <div className="flex items-center gap-2">
        <HardDrive className="h-5 w-5 text-slate-500" />
        <h3 className="font-bold text-slate-900">Browser storage</h3>
      </div>

      <p className="text-sm text-slate-600">
        RheumHub stores a small number of items in your browser's local storage for essential functional purposes — such as remembering your role and consent choices. No tracking data is stored. You can see exactly what is stored below and clear it at any time.
      </p>

      <div className="overflow-x-auto rounded-xl border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-4 py-2.5 font-semibold text-slate-700 border-b border-slate-100">Item</th>
              <th className="px-4 py-2.5 font-semibold text-slate-700 border-b border-slate-100">Current value</th>
            </tr>
          </thead>
          <tbody>
            {BROWSER_KEYS.map(({ key, label }) => (
              <tr key={key} className="border-b border-slate-50 last:border-b-0">
                <td className="px-4 py-2.5 text-slate-700">{label}</td>
                <td className="px-4 py-2.5">
                  <span
                    className={
                      values[key] === "Not set"
                        ? "text-slate-400 italic"
                        : "text-slate-800 font-medium"
                    }
                  >
                    {values[key] ?? "Not set"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmClear ? (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
            <div>
              <p className="font-semibold">Clear all browser data?</p>
              <p className="mt-0.5 text-amber-800">
                This will remove your stored role, consent choices, and other preferences from this browser.{" "}
                {standalone
                  ? "You will be returned to the RheumHub home page."
                  : "You will be redirected to the role selection page."}
              </p>
              {isSignedIn && (
                <p className="mt-2 text-amber-800">
                  You are currently signed in. Clearing browser data only removes local preferences — your RheumHub account and all server-side data remain completely untouched. You can sign back in at any time.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleClear}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Yes, clear browser data
            </Button>
            <Button variant="outline" onClick={() => setConfirmClear(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setConfirmClear(true)}
          className="border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear all browser data
        </Button>
      )}

      <p className="text-xs text-slate-400">
        {standalone
          ? "Clearing browser data does not delete any data stored on the RheumHub servers — sign in and visit your Settings page to delete server-side data."
          : "Clearing browser data does not delete any data stored on the RheumHub servers — use the \"Delete all my data\" section above for that."}
      </p>
    </section>
  );
}
